import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import FanVerseLogo from "@/components/FanVerseLogo";
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030712] px-4">
        <div className="relative z-10 w-full max-w-md text-center">
          <Link to="/" className="flex items-center justify-center gap-2 text-yellow-500 mb-8">
            <FanVerseLogo size={44} showText={false} />
          </Link>
          <div className="card-surface p-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2 font-['Outfit']">Invalid Reset Link</h1>
            <p className="text-slate-400 text-sm mb-6">This password reset link is invalid or has expired.</p>
            <Link to="/forgot-password">
              <Button className="btn-primary w-full" data-testid="request-new-link">Request a New Link</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      toast.success("Password reset! You can now log in.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Reset failed. The link may have expired.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#030712] px-4">
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/f60715c2-3b6b-4c99-a6c8-5c96b98e6cf9/images/6a0a81e49ef10480dd8f68fed5fd73786eac1a2e88374dc1978201d1210e22e0.png"
          alt="Background"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/80 to-[#030712]/60" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 text-yellow-500 mb-8">
            <FanVerseLogo size={44} showText={false} />
        </Link>

        <div className="card-surface p-8">
          {success ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2 font-['Outfit']">Password Reset!</h1>
              <p className="text-slate-400 text-sm mb-6">
                Your password has been updated. You can now log in with your new password.
              </p>
              <Button onClick={() => navigate("/login")} className="btn-primary w-full" data-testid="go-to-login">
                Go to Login
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-white text-center mb-2 font-['Outfit']">Set New Password</h1>
              <p className="text-slate-400 text-center text-sm mb-6">
                Enter your new password below.
              </p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-500 px-4 py-3 text-sm mb-6" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="password" className="text-slate-300 text-sm">{t("auth.newPassword")}</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="pl-10 pr-10 input-dark"
                      required
                      minLength={6}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm" className="text-slate-300 text-sm">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="confirm"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      className="pl-10 input-dark"
                      required
                      minLength={6}
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-3"
                  data-testid="reset-submit"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>

              <p className="text-center text-slate-400 text-sm mt-6">
                <Link to="/login" className="text-yellow-500 hover:underline">
                  Back to login
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
